/**
 * Help system state management with keyboard shortcut.
 * Press ? to toggle the help drawer (ignored while typing in inputs).
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useRouterState } from "@tanstack/react-router";
import { HELP_CONTENT, type HelpPageContent } from "@/data/help-content";

const HELP_ROUTES = Object.keys(HELP_CONTENT);

export function useHelp() {
  const [isOpen, setIsOpen] = useState(false);
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Match route to help content (longest matching prefix wins)
  const content: HelpPageContent | null = useMemo(() => {
    // Try exact match first, then prefix match (sorted longest-first for detail routes)
    const sorted = [...HELP_ROUTES].sort((a, b) => b.length - a.length);
    const match = sorted.find((path) => currentPath.startsWith(path));
    return match ? HELP_CONTENT[match] ?? null : null;
  }, [currentPath]);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Keyboard shortcut: ? key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "?") return;

      const target = e.target as HTMLElement;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      e.preventDefault();
      setIsOpen((prev) => !prev);
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return { isOpen, content, toggle, open, close };
}
